import { QueryKey, useQuery } from '@tanstack/react-query';
import { getPendingSlidesCount, getSlides } from 'api/slides';
import { Permission } from 'interfaces/permissionOption';
import { Procedure } from 'interfaces/procedure';
import { Slide } from 'interfaces/slide';
import { ceil, compact, find, flatMap, isEmpty, map, some } from 'lodash';
import { stringify } from 'qs';
import { useMemo } from 'react';
import { filterSearchOptions } from 'services/matchSort';
import { BooleanParam, encodeQueryParams, useQueryParam } from 'use-query-params';
import { useSearchIndex } from 'utils/queryHooks/useSearchIndex';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { DEFAULT_PAGE_SIZE, filtersQueryParamsSchema } from 'utils/useEncodedFilters/constants';
import { useGetAllPaginatedQueries } from 'utils/useGetAllPaginatedQueries';
import { usePermissions } from 'utils/usePermissions';
import { canQueryPendingSlides } from './pendingSlidesPermissions';

const pendingSlideSearchKeys = ['slides.*.id', 'slides.*.fileName'];

export const getPendingSlidesBaseParams = ({
  filters,
  labId,
  searchTerm,
  slideIdsToInclude,
  includeHidden,
}: {
  filters: Record<string, any> | null | undefined;
  labId: string;
  searchTerm?: string | null;
  slideIdsToInclude?: (string | number | null)[] | null;
  includeHidden?: boolean;
}) => ({
  filters: { ...filters },
  labId,
  ...(searchTerm ? { searchTerm } : {}),
  ...(slideIdsToInclude?.length ? { slideIdsToInclude } : {}),
  ...(includeHidden ? { includeHidden: true } : {}),
});

export const getPendingSlidesParamsString = ({
  queryParamsSchema,
  params,
}: {
  queryParamsSchema: Record<string, any>;
  params: Record<string, any>;
}) =>
  stringify(encodeQueryParams({ ...queryParamsSchema, includeHidden: BooleanParam }, params), {
    arrayFormat: 'repeat',
  });

const getPendingSlidesCountQueryKey = (queryParams: string): QueryKey => ['pendingSlidesCount', queryParams];

const getPendingSlidesQueryKeyPrefix = (baseQueryParams: string): QueryKey => ['pendingSlides', baseQueryParams];

const getPendingSlidesPageQueryKeyPrefix = (baseQueryParams: string): QueryKey => [
  ...getPendingSlidesQueryKeyPrefix(baseQueryParams),
  'page',
];

const getPendingSlidesPageQueryKey = ({
  baseQueryParams,
  pageQueryParams,
}: {
  baseQueryParams: string;
  pageQueryParams: string;
}): QueryKey => [...getPendingSlidesPageQueryKeyPrefix(baseQueryParams), pageQueryParams];

export const usePendingSlideIdsFromSearchTerm = ({
  enabled,
  searchTerm,
  slideIdsToInclude,
}: {
  enabled?: boolean;
  searchTerm?: string | null;
  slideIdsToInclude?: (string | number | null)[] | null;
}) => {
  const shouldDeriveSlideIdsFromSearch = enabled && Boolean(searchTerm) && isEmpty(slideIdsToInclude);
  const searchIndexQuery = useSearchIndex({ enabled: shouldDeriveSlideIdsFromSearch });

  const derivedSlideIdsToInclude = useMemo(() => {
    if (!shouldDeriveSlideIdsFromSearch) {
      return slideIdsToInclude;
    }

    const activeSearchTerm = searchTerm || '';

    if (!searchIndexQuery.index) {
      return [activeSearchTerm];
    }

    const slideSearchItems = flatMap(searchIndexQuery.index, (caseItem) =>
      map(caseItem?.slides, (slide) => ({ ...caseItem, slides: [slide] }))
    );
    const matchingSlideIds = compact(
      map(
        filterSearchOptions(pendingSlideSearchKeys, slideSearchItems, activeSearchTerm),
        (caseItem) => caseItem.slides?.[0]?.id
      )
    );

    return isEmpty(matchingSlideIds) ? [activeSearchTerm] : matchingSlideIds;
  }, [searchIndexQuery.index, searchTerm, shouldDeriveSlideIdsFromSearch, slideIdsToInclude]);

  return {
    slideIdsToInclude: derivedSlideIdsToInclude,
    isResolvingSearchTermSlideIds: shouldDeriveSlideIdsFromSearch && searchIndexQuery.isLoading,
  };
};

const usePendingSlidesCount = ({
  enabled,
  queryParamsSchema,
  baseParams,
}: {
  enabled: boolean;
  queryParamsSchema: Record<string, any>;
  baseParams: Record<string, any>;
}) => {
  const encodedBaseParams = useMemo(
    () => getPendingSlidesParamsString({ queryParamsSchema, params: baseParams }),
    [queryParamsSchema, baseParams]
  );

  const query = useQuery(
    getPendingSlidesCountQueryKey(encodedBaseParams),
    ({ signal }) => getPendingSlidesCount(encodedBaseParams, signal),
    {
      enabled,
    }
  );

  return {
    encodedBaseParams,
    pendingSlidesCount: query.data || 0,
    pendingSlidesCountQuery: query,
  };
};

export const useLabPendingSlidesCount = () => {
  const { labId } = useCurrentLabId();
  const { hasPermission } = usePermissions();
  const canViewPendingSlides = hasPermission(Permission.ViewPendingSlides);
  const baseParams = useMemo(() => getPendingSlidesBaseParams({ filters: undefined, labId }), [labId]);
  const { pendingSlidesCount, pendingSlidesCountQuery } = usePendingSlidesCount({
    enabled: canViewPendingSlides,
    queryParamsSchema: filtersQueryParamsSchema,
    baseParams,
  });

  return {
    totalPendingSlides: pendingSlidesCount,
    isLoading: pendingSlidesCountQuery.isLoading,
  };
};

export const useStudyPendingSlidesCount = ({ studyId, enabled = true }: { studyId?: string; enabled?: boolean }) => {
  const { labId } = useCurrentLabId();
  const { hasPermission } = usePermissions();
  const canQueryStudyPendingSlides = canQueryPendingSlides({ hasPermission, studyId });
  const baseParams = useMemo(
    () => getPendingSlidesBaseParams({ filters: studyId ? { studyId } : undefined, labId }),
    [labId, studyId]
  );
  const { pendingSlidesCount, pendingSlidesCountQuery } = usePendingSlidesCount({
    enabled: enabled && canQueryStudyPendingSlides,
    queryParamsSchema: filtersQueryParamsSchema,
    baseParams,
  });
  const isLoading =
    pendingSlidesCountQuery.isLoading ||
    (pendingSlidesCountQuery.isFetching && typeof pendingSlidesCountQuery.data === 'undefined');

  return {
    totalPendingSlides: pendingSlidesCount,
    canQueryPendingSlides: canQueryStudyPendingSlides,
    isLoading,
    isRefreshing: pendingSlidesCountQuery.isFetching && !isLoading,
    isError: pendingSlidesCountQuery.isError,
    isSuccess: pendingSlidesCountQuery.isSuccess && typeof pendingSlidesCountQuery.data === 'number',
  };
};

export const usePendingSlides = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { labId } = useCurrentLabId();
  const { queryParams, queryParamsSchema } = useEncodedFilters();
  const [pendingSlidesMode] = useQueryParam('pendingSlidesMode', BooleanParam);
  const { hasPermission } = usePermissions();
  const canQueryCurrentPendingSlides = canQueryPendingSlides({
    hasPermission,
    studyId: queryParams.filters?.studyId,
  });
  const page: number = queryParams.page || 1;
  const pageSize: number = queryParams.pageSize || DEFAULT_PAGE_SIZE;
  const { slideIdsToInclude, isResolvingSearchTermSlideIds } = usePendingSlideIdsFromSearchTerm({
    enabled: canQueryCurrentPendingSlides && (Boolean(pendingSlidesMode) || Boolean(enabled)),
    searchTerm: queryParams.searchTerm,
    slideIdsToInclude: queryParams.slideIdsToInclude,
  });
  const baseParams = useMemo(
    () =>
      getPendingSlidesBaseParams({
        filters: queryParams.filters,
        labId,
        searchTerm: queryParams.searchTerm,
        slideIdsToInclude,
      }),
    [queryParams.filters, labId, queryParams.searchTerm, slideIdsToInclude]
  );
  const { encodedBaseParams, pendingSlidesCount } = usePendingSlidesCount({
    enabled: enabled && canQueryCurrentPendingSlides && !isResolvingSearchTermSlideIds,
    queryParamsSchema,
    baseParams,
  });
  const pageParams = useMemo(
    () => ({ ...baseParams, page, pageSize, skipPagination: false }),
    [baseParams, page, pageSize]
  );
  const pendingSlidesQueryParams = useMemo(
    () => getPendingSlidesParamsString({ queryParamsSchema, params: pageParams }),
    [queryParamsSchema, pageParams]
  );
  const pendingSlidesPageQueryKeyPrefix = useMemo(
    () => getPendingSlidesPageQueryKeyPrefix(encodedBaseParams),
    [encodedBaseParams]
  );
  const pendingSlidesPageQueryKey = useMemo(
    () =>
      getPendingSlidesPageQueryKey({
        baseQueryParams: encodedBaseParams,
        pageQueryParams: pendingSlidesQueryParams,
      }),
    [encodedBaseParams, pendingSlidesQueryParams]
  );

  const {
    data: pendingSlides,
    isLoading: isPendingSlidesLoading,
    error: pendingSlidesError,
    ...pendingSlidesQuery
  } = useQuery(
    pendingSlidesPageQueryKey,
    ({ signal }) => getSlides({ queryParams: pendingSlidesQueryParams, pending: true }, signal),
    {
      enabled:
        canQueryCurrentPendingSlides &&
        !isResolvingSearchTermSlideIds &&
        (Boolean(pendingSlidesMode) || Boolean(enabled)),
      retry: false,
    }
  );

  const mockCasesOfPendingSlides = useMemo(() => {
    return createIndividualCasesForSlides(pendingSlides, labId);
  }, [pendingSlides, labId]);

  return {
    mockCasesOfPendingSlides,
    allMockCasesOfPendingSlides: mockCasesOfPendingSlides,
    allPendingSlide: pendingSlides,
    isPendingSlidesLoading: isPendingSlidesLoading || isResolvingSearchTermSlideIds,
    pendingSlidesError,
    totalPendingSlides: pendingSlidesCount,
    pendingSlidesInPreviousPages: pageSize * (page - 1),
    pendingCasesInPreviousPages: pageSize * (page - 1),
    pendingSlidesPageQueryKeyPrefix,
    pendingSlidesPageQueryKey,
    pendingSlidesQuery,
  };
};

export const useAllPendingSlides = (
  {
    enabled,
    scope = 'currentFilters',
    studyId,
    includeHidden = false,
  }: {
    enabled?: boolean;
    scope?: 'currentFilters' | 'study' | 'lab';
    studyId?: string;
    includeHidden?: boolean;
  } = { enabled: false }
) => {
  const { labId } = useCurrentLabId();
  const { queryParams, queryParamsSchema } = useEncodedFilters();
  const { hasPermission } = usePermissions();
  const canQueryRequestedPendingSlides = canQueryPendingSlides({
    hasPermission,
    studyId: scope === 'lab' ? undefined : scope === 'study' ? studyId : queryParams.filters?.studyId,
  });
  const page: number = queryParams.page || 1;
  const pageSize: number = queryParams.pageSize || DEFAULT_PAGE_SIZE;
  const shouldFetchAllPendingSlides = Boolean(enabled) && canQueryRequestedPendingSlides;
  const { slideIdsToInclude, isResolvingSearchTermSlideIds } = usePendingSlideIdsFromSearchTerm({
    enabled: shouldFetchAllPendingSlides && scope === 'currentFilters',
    searchTerm: scope === 'currentFilters' ? queryParams.searchTerm : undefined,
    slideIdsToInclude: scope === 'currentFilters' ? queryParams.slideIdsToInclude : undefined,
  });
  const baseParams = useMemo(
    () =>
      getPendingSlidesBaseParams({
        filters: scope === 'lab' ? undefined : scope === 'study' ? { studyId } : queryParams.filters,
        labId,
        includeHidden,
        searchTerm: scope === 'currentFilters' ? queryParams.searchTerm : undefined,
        slideIdsToInclude,
      }),
    [scope, studyId, queryParams.filters, labId, queryParams.searchTerm, slideIdsToInclude, includeHidden]
  );

  const { encodedBaseParams, pendingSlidesCount, pendingSlidesCountQuery } = usePendingSlidesCount({
    enabled: shouldFetchAllPendingSlides && !isResolvingSearchTermSlideIds,
    queryParamsSchema,
    baseParams,
  });

  const totalPages =
    shouldFetchAllPendingSlides && !isResolvingSearchTermSlideIds ? ceil(pendingSlidesCount / pageSize) : 0;
  const currentPage = totalPages > 0 ? Math.min(Math.max(page - 1, 0), totalPages - 1) : 0;

  const paginatedQueries = useGetAllPaginatedQueries<Slide, Slide[], string>({
    enabled: shouldFetchAllPendingSlides && !isResolvingSearchTermSlideIds,
    queriedEntityName: 'pending slides',
    retry: false,
    currentPage,
    totalPages,
    numSurroundingPagesToPrefetch: 0,
    pageQueryFn: (pageQueryParams, { signal }) => getSlides({ queryParams: pageQueryParams, pending: true }, signal),
    getPageArgs: (pageIndex) =>
      getPendingSlidesParamsString({
        queryParamsSchema,
        params: {
          ...baseParams,
          page: pageIndex + 1,
          pageSize,
          skipPagination: false,
        },
      }),
    getQueryKeyForPage: (pageIndex) =>
      getPendingSlidesPageQueryKey({
        baseQueryParams: encodedBaseParams,
        pageQueryParams: getPendingSlidesParamsString({
          queryParamsSchema,
          params: {
            ...baseParams,
            page: pageIndex + 1,
            pageSize,
            skipPagination: false,
          },
        }),
      }),
  });

  const pendingSlides = useMemo(() => flatMap(paginatedQueries.items, ({ items }) => items), [paginatedQueries.items]);

  const isPendingSlidesLoading =
    shouldFetchAllPendingSlides &&
    (isResolvingSearchTermSlideIds ||
      pendingSlidesCountQuery.isLoading ||
      pendingSlidesCountQuery.isFetching ||
      paginatedQueries.isPrefetchingAllPages ||
      (totalPages > 0 && some(paginatedQueries.queries, (query) => query.isLoading || query.isFetching)));

  const allMockCasesOfPendingSlides = useMemo(
    () => createIndividualCasesForSlides(pendingSlides, labId),
    [pendingSlides, labId]
  );
  const pendingSlidesError =
    pendingSlidesCountQuery.error || find(paginatedQueries.queries, (query) => Boolean(query.error))?.error;

  return {
    allPendingSlide: pendingSlides,
    allMockCasesOfPendingSlides,
    canQueryPendingSlides: canQueryRequestedPendingSlides,
    isPendingSlidesLoading,
    pendingSlidesError,
    totalPendingSlides: pendingSlidesCount,
    pendingSlidesQuery: {
      isFetching:
        pendingSlidesCountQuery.isFetching ||
        paginatedQueries.isPrefetchingAllPages ||
        some(paginatedQueries.queries, 'isFetching'),
      isLoading: isPendingSlidesLoading,
    },
  };
};

// Strips a trailing file extension only (e.g. "slide-a.svs" -> "slide-a"); names without one are unchanged.
const stripFileExtension = (fileName?: string | null) => (fileName || '').replace(/\.[^./\\]+$/, '');

export const createIndividualCasesForSlides = (slides: Slide[] | undefined | null, labId: string): Procedure[] => {
  return map(slides || [], (slide, index) => {
    const caseLabel = stripFileExtension(slide.originalFileName) || slide.id;
    return {
      id: index,
      isPendingSlideMockCase: true,
      labId: labId,
      name: caseLabel,
      batchDate: undefined,
      createdAt: slide?.createdAt,
      diagnosis: undefined,
      label: caseLabel,
      cancerTypeId: 9, // N/A
      cancerSubtypes: [],
      clinicalData: undefined,
      internallyApproved: undefined,
      features: undefined,
      studyId: undefined,
      slides: [slide],
    } as unknown as Procedure;
  });
};
